import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft } from 'lucide-react'
import { searchParam, type SessionRole } from '../../core/index.js'
import { CensusCard } from './CensusCard.js'
import { FacilityPublishShifts } from './PublishShiftsCard.js'
import { ScheduleBoardCard } from './ScheduleBoardCard.js'
import { ScheduleRunCard } from './ScheduleRunCard.js'
import { MonthOverview, type MonthDetail } from './MonthOverview.js'

type MonthSection = 'overview' | MonthDetail

const SECTIONS: readonly MonthSection[] = ['overview', 'health', 'build', 'board', 'census', 'publish']
const DETAIL_TITLE: Record<MonthDetail, string> = { health: 'The month’s health', build: 'Build and release', board: 'After the release', census: 'Census planning', publish: 'Publish shifts' }

/** The section a link asked for (`?detail=`, the Month card's and Today's buttons): `unfilled` and `release` are the build's; none asked, null. */
function askedSection(): MonthSection | null {
  const detail = searchParam('detail')
  if (detail === 'unfilled' || detail === 'release') {
    return 'build'
  }
  return SECTIONS.includes(detail as MonthSection) ? (detail as MonthSection) : null
}

function requestedSection(): MonthSection {
  return askedSection() ?? 'overview'
}

interface MonthPanelProps {
  /** Whether the Month tab is on screen: its views open the loading window only then. */
  shown: boolean
  facilityId: string
  dashboardHref: string
  onAddWorker?: () => void
  role: SessionRole
  hasWorkers: boolean
  configHref: string
  /** The month's health and the facility in bold strokes, composed by the page. */
  overview: ReactNode
}

/**
 * The Dashboard's Month tab (facility operations dashboard plan §3): one overview of the month being built, as the
 * mockup draws it, with no strip of sub-tabs - the detail (the findings in full, the build and release, the board after
 * the release, census planning, the published shifts) opens from the overview's links in its place, with a way back.
 * Every section stays mounted so a detail keeps its state between visits; the build and the board follow the same month.
 */
export function MonthPanel({ shown: tabShown, facilityId, dashboardHref, onAddWorker, role, hasWorkers, configHref, overview }: MonthPanelProps) {
  const [section, setSection] = useState<MonthSection>(requestedSection)
  // A link into the Month tab while the Dashboard is open (Open the board on a finding) opens its detail at once, as
  // the tabs follow `?tab=`; before, only a fresh load read `?detail=`, so the button seemed to do nothing for a while.
  useEffect(() => {
    const follow = (): void => {
      const asked = askedSection()
      if (asked) {
        setSection(asked)
      }
    }
    window.addEventListener('popstate', follow)
    return () => window.removeEventListener('popstate', follow)
  }, [])
  // Opening a detail (or going back) starts at its top, not where the overview was scrolled to.
  const opened = useRef(section)
  useEffect(() => {
    if (opened.current !== section && tabShown) {
      window.scrollTo({ top: 0 })
    }
    opened.current = section
  }, [section, tabShown])
  // The board follows the month the run card shows, kept with the facility it belongs to.
  const [shown, setShown] = useState<{ facilityId: string; period: string } | null>(null)
  const period = shown?.facilityId === facilityId ? shown.period : null
  const onPeriodChange = useCallback((next: string) => setShown({ facilityId, period: next }), [facilityId])
  return (
    <div className="flex flex-col gap-5">
      {section !== 'overview' && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="inline-flex items-center gap-1 text-xs font-bold text-marine hover:text-marine-dk" onClick={() => setSection('overview')}>
            <ChevronLeft className="h-4 w-4" />
            Back to the month
          </button>
          <h2 className="text-lg font-black text-ink">{DETAIL_TITLE[section]}</h2>
        </div>
      )}
      <div hidden={section !== 'overview'}>
        <MonthOverview shown={tabShown && section === 'overview'} facilityId={facilityId} dashboardHref={dashboardHref} configHref={configHref} onAddWorker={onAddWorker} onOpen={setSection} />
      </div>
      <div hidden={section !== 'health'}>{overview}</div>
      <div hidden={section !== 'build'}>
        <ScheduleRunCard key={facilityId} facilityId={facilityId} hasWorkers={hasWorkers} onPeriodChange={onPeriodChange} role={role} onPublishShifts={() => setSection('publish')} />
      </div>
      <div hidden={section !== 'board'}>
        <ScheduleBoardCard shown={tabShown && section === 'board'} facilityId={facilityId} period={period} />
      </div>
      <div hidden={section !== 'census'}>
        <CensusCard key={facilityId} facilityId={facilityId} configHref={configHref} />
      </div>
      <div hidden={section !== 'publish'}>
        <FacilityPublishShifts facilityId={facilityId} />
      </div>
    </div>
  )
}
